import { useState, createContext, useContext } from "react";
import { useTheme } from "@repo/tokens";
import { resolveThemeToken } from "../../utils/theme";
import { Text } from "../../primitives/text";
import { VStack } from "../../primitives/stack";
import { type CheckboxProps, type CheckboxGroupProps } from "./types";
import { Icon } from "../icon";

// --- Group Context ---
interface CheckboxGroupContextValue {
  value: string[];
  onChange: (checked: boolean, val: string) => void;
  isDisabled?: boolean;
  size?: "sm" | "md" | "lg";
}
const CheckboxGroupContext = createContext<CheckboxGroupContextValue | null>(
  null,
);

// --- Checkbox Component ---
export const Checkbox = ({
  isChecked: checkedProp,
  defaultChecked,
  onChange,
  value,
  label,
  size = "md",
  isDisabled,
  isInvalid,
  isIndeterminate,
  id,
  name,
  testID,
  accessibilityLabel,

  selectedLabelColor = null,
  unSelectedLabelColor = null,

  selectedIconColor = null,
  // unSelectedIconColor = null,

  selectedIconBgColor = null,
  unSelectedIconBgColor = null,

  selectedIconBorderColor = null,
  unSelectedIconBorderColor = null,

  boxSize: boxSizeProp,
  boxBorderRadius,
  boxBorderWidth,
  iconSize,

  // Font Styling
  fontFamily = undefined,
  fontWeight = undefined,
  fontStyle = undefined,
  fontSize = undefined,
  lineHeight = undefined,
  letterSpacing = undefined,
  verticalAlign = undefined,
  labelStyle = undefined,
  containerStyle = undefined,

  className,
}: CheckboxProps) => {
  const { theme } = useTheme();
  const group = useContext(CheckboxGroupContext);

  // Group Logic Override
  const computedIsDisabled = group?.isDisabled || isDisabled;
  const computedSize = group?.size || size;

  const [internalChecked, setInternalChecked] = useState(
    defaultChecked || false,
  );
  const isChecked = group
    ? value
      ? group.value.includes(value)
      : false
    : checkedProp !== undefined
      ? checkedProp
      : internalChecked;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (computedIsDisabled) return;
    const newChecked = e.target.checked;

    if (group && value) {
      group.onChange(newChecked, value);
    } else {
      setInternalChecked(newChecked);
      onChange?.(newChecked);
    }
  };

  // Styling Tokens

  // We use theme.control.height/padding if we can map it, but checkbox size is usually smaller than input height.
  // Let's stick to these sensible defaults or add checkbox specific tokens later?
  // Plan said: "control.height.sm". 32px is too big for a checkbox box. input height != checkbox box size.
  // I will use hardcoded for now matching standard 16/20/24 px visual.

  const boxSize =
    boxSizeProp ??
    (computedSize === "sm" ? 16 : computedSize === "lg" ? 24 : 20);
  const textSize = computedSize === "sm" ? "caption" : "body";

  // Colors
  let borderColor = unSelectedIconBorderColor ?? theme.colors.neutral[20];
  let bgColor = unSelectedIconBgColor ?? "surface.input";
  // let iconColor = unSelectedIconColor ?? "text.inverse";

  if (computedIsDisabled) {
    borderColor = "control.border.disabled";
    bgColor = "control.background.disabled";
    // iconColor = "text.disabled";
  } else if (isInvalid) {
    borderColor = "control.border.error";
  } else if (isChecked || isIndeterminate) {
    borderColor = selectedIconBorderColor ?? theme.colors.primary[40];
    bgColor = selectedIconBgColor ?? theme.colors.primary[40];
  }
  // Hover logic is tricky in inline styles without CSS modules/styled-components.
  // Relying on browser native hover for cursor, but border color change requires CSS.
  // For this MVP, we skip complex hover states in inline styles unless we use a wrapper with onMouseEnter.

  const resolvedBorder = resolveThemeToken(theme, borderColor);
  const resolvedBg =
    isChecked || isIndeterminate
      ? resolveThemeToken(theme, bgColor)
      : "transparent";

  return (
    <label
      className={className}
      style={{
        display: "inline-flex",
        verticalAlign: "top",
        alignItems: "center",
        cursor: computedIsDisabled ? "not-allowed" : "pointer",
        opacity: computedIsDisabled ? 0.6 : 1,
        ...(containerStyle || {}),
      }}
      data-testid={testID}
    >
      <div
        style={{
          position: "relative",
          width: boxSize,
          height: boxSize,
          minWidth: boxSize,
          minHeight: boxSize,
          marginRight: 16, // Added margin to separate from label
        }}
      >
        <input
          type="checkbox"
          id={id}
          name={name}
          checked={isChecked}
          onChange={handleChange}
          disabled={computedIsDisabled}
          aria-label={accessibilityLabel || label}
          aria-invalid={isInvalid}
          style={{
            position: "absolute",
            inset: 0,
            opacity: 0,
            margin: 0,
            cursor: "inherit",
          }}
        />

        {/* Custom Box */}
        <div
          style={{
            width: "100%",
            height: "100%",
            boxSizing: "border-box",
            border: `${boxBorderWidth ?? 2}px solid ${resolvedBorder}`,
            backgroundColor: resolvedBg,
            borderRadius: boxBorderRadius ?? theme.radii.xs, // Checkboxes usually tighter radius
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            //transition: "all 0.2s ease",
          }}
        >
          {(isIndeterminate || isChecked) && (
            <Icon
              name={isIndeterminate ? "indeterminateIcon" : "checkedIcon"}
              color={selectedIconColor ?? "white"}
              size={iconSize ?? boxSize}
            />
          )}
        </div>
      </div>

      {label && (
        <Text
          variant={textSize}
          color={
            computedIsDisabled
              ? "text.disabled"
              : isChecked
                ? (selectedLabelColor ?? "text.primary")
                : (unSelectedLabelColor ?? "text.primary")
          }
          style={{
            lineHeight: lineHeight || "1.2",
            fontFamily: fontFamily || undefined,
            fontWeight: fontWeight || undefined,
            fontStyle: fontStyle || undefined,
            fontSize: fontSize || undefined,
            letterSpacing: letterSpacing || undefined,
            verticalAlign: verticalAlign || undefined,
            ...(labelStyle || {}),
          }}
        >
          {label}
        </Text>
      )}
    </label>
  );
};

// --- Group Component ---
export const CheckboxGroup = ({
  value,
  defaultValue,
  onChange,
  isDisabled,
  children,
  label,
  direction = "column",
  gap = "sm",
}: CheckboxGroupProps) => {
  const [internalValue, setInternalValue] = useState<string[]>(
    defaultValue || [],
  );

  const currentValue = value !== undefined ? value : internalValue;

  const handleChange = (checked: boolean, itemValue: string) => {
    let newValue: string[];
    if (checked) {
      newValue = [...currentValue, itemValue];
    } else {
      newValue = currentValue.filter((v) => v !== itemValue);
    }

    if (value === undefined) {
      setInternalValue(newValue);
    }
    onChange?.(newValue);
  };

  return (
    <CheckboxGroupContext.Provider
      value={{ value: currentValue, onChange: handleChange, isDisabled }}
    >
      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        {label && (
          <legend style={{ marginBottom: 8 }}>
            <Text variant="caption" color="text.secondary">
              {label}
            </Text>
          </legend>
        )}
        <VStack
          gap={gap}
          style={
            direction === "row"
              ? { flexDirection: "row", flexWrap: "wrap" }
              : {}
          }
        >
          {children}
        </VStack>
      </fieldset>
    </CheckboxGroupContext.Provider>
  );
};
